import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { createProjectDonationByAdmin } from '../../../controllers/paymentsController';
import { searchMembersWithSubMembers } from '../../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../../api/httpClientGroups';
import { ProjectPaymentMethod } from '../projectDonationLogoUtils';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const CreateProjectDonationDialog = ({ open, onClose, onCreated, projects = [] }) => {
  const [form, setForm] = useState({
    projectId: '',
    payerPerson: null,
    payerPersonType: 0,
    amount: '',
    currency: 'TRY',
    paymentMethod: ProjectPaymentMethod.Cash,
    referenceNumber: '',
    description: '',
    paymentProofUrl: '',
    paymentDate: '',
  });
  const [payerQuery, setPayerQuery] = useState('');
  const [payerOptions, setPayerOptions] = useState([]);
  const [payerLoading, setPayerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    setForm({
      projectId: projects[0]?.id || '',
      payerPerson: null,
      payerPersonType: 0,
      amount: '',
      currency: 'TRY',
      paymentMethod: ProjectPaymentMethod.Cash,
      referenceNumber: '',
      description: '',
      paymentProofUrl: '',
      paymentDate: '',
    });
    setPayerQuery('');
    setPayerOptions([]);
  }, [open, projects]);

  useEffect(() => {
    if (!open || !payerQuery || payerQuery.trim().length < 2) {
      setPayerOptions([]);
      return;
    }
    let active = true;
    setPayerLoading(true);
    searchMembersWithSubMembers(payerQuery.trim())
      .then((data) => {
        if (!active) return;
        const flattened = [];
        (Array.isArray(data) ? data : []).forEach((item) => {
          if (!item) return;
          flattened.push({ id: item.id, fullName: item.fullName, profilePictureUrl: item.profilePictureUrl, type: 0 });
          (item.subMembers || []).forEach((s) => {
            if (!s) return;
            flattened.push({ id: s.id, fullName: s.fullName, profilePictureUrl: s.profilePictureUrl, type: 1 });
          });
        });
        setPayerOptions(flattened);
      })
      .finally(() => { if (active) setPayerLoading(false); });
    return () => { active = false; };
  }, [payerQuery, open]);

  const handleSubmit = async () => {
    if (!form.projectId) {
      setError('Proje seçiniz');
      return;
    }
    if (!form.payerPerson?.id) {
      setError('Bağışçı seçiniz');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setError('Geçerli bir tutar giriniz');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        projectId: form.projectId,
        payerPersonId: form.payerPerson.id,
        payerPersonType: form.payerPerson.type ?? 0,
        amount: Number(form.amount),
        currency: form.currency || 'TRY',
        paymentMethod: form.paymentMethod,
        referenceNumber: form.referenceNumber?.trim() || null,
        description: form.description?.trim() || null,
        paymentProofUrl: form.paymentProofUrl?.trim() || null,
        paymentDate: form.paymentDate ? new Date(form.paymentDate).toISOString() : null,
      };
      await createProjectDonationByAdmin(payload);
      toast.success(
        form.paymentMethod === ProjectPaymentMethod.BankTransfer
          ? 'Proje bağışı oluşturuldu. Admin onayı bekleniyor.'
          : 'Proje bağışı oluşturuldu ve onaylandı.',
      );
      onClose();
      if (onCreated) onCreated();
    } catch (e) {
      setError(e?.message || 'Proje bağışı oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const isBankTransfer = form.paymentMethod === ProjectPaymentMethod.BankTransfer;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', pr: 3 }}>
        Manuel Proje Bağışı
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info" sx={{ fontSize: '13px' }}>
            Manuel kayıtlar Logo fişi oluşturmaz. Havale seçilirse onay kuyruğuna düşer.
          </Alert>
          <TextField
            select
            label="Proje"
            value={form.projectId}
            onChange={(e) => setForm((p) => ({ ...p, projectId: e.target.value }))}
            fullWidth
            sx={fieldSx}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}{p.logoCariCode ? '' : ' (Logo cari yok)'}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={payerOptions}
            loading={payerLoading}
            value={form.payerPerson}
            onChange={(_, v) => setForm((p) => ({
              ...p,
              payerPerson: v,
              payerPersonType: v?.type ?? 0,
            }))}
            onInputChange={(_, v) => setPayerQuery(v)}
            getOptionLabel={(o) => o?.fullName || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderOption={(props, option) => (
              <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                <Typography variant="body2">{option.fullName}</Typography>
                <Chip size="small" label={option.type === 1 ? 'Yakın' : 'Üye'} sx={{ ml: 'auto' }} />
              </Stack>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Bağışçı" placeholder="Ara..." sx={fieldSx} />
            )}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tutar"
                type="number"
                inputProps={{ step: '0.01', min: 0.01 }}
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                fullWidth
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Ödeme Yöntemi"
                value={form.paymentMethod}
                onChange={(e) => setForm((p) => ({ ...p, paymentMethod: Number(e.target.value) }))}
                fullWidth
                sx={fieldSx}
              >
                <MenuItem value={0}>Nakit</MenuItem>
                <MenuItem value={1}>Banka Havalesi/EFT</MenuItem>
                <MenuItem value={2}>Kredi Kartı</MenuItem>
                <MenuItem value={3}>Online Ödeme</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Referans No"
                value={form.referenceNumber}
                onChange={(e) => setForm((p) => ({ ...p, referenceNumber: e.target.value }))}
                fullWidth
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ödeme Tarihi"
                type="datetime-local"
                value={form.paymentDate}
                onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Açıklama"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                multiline
                rows={2}
                fullWidth
                sx={fieldSx}
              />
            </Grid>
            {isBankTransfer && (
              <Grid item xs={12}>
                <TextField
                  label="Ödeme Kanıtı URL"
                  value={form.paymentProofUrl}
                  onChange={(e) => setForm((p) => ({ ...p, paymentProofUrl: e.target.value }))}
                  fullWidth
                  placeholder="https://..."
                  sx={fieldSx}
                />
              </Grid>
            )}
          </Grid>
          {isBankTransfer && (
            <Typography sx={{ fontSize: '12px', color: '#a3a3a3' }}>
              Havale kayıtları onay bekliyor durumuna düşer.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>İptal</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: 'none', backgroundColor: '#0E4D94' }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectDonationDialog;
